import {
  Filter,
  FilterValue,
  InputType,
  RowVisibility,
} from "../../types/filter";
import { TrackingProps } from "../../types/component";
import { useTranslation } from "@coworker/locales";
import FixaButton from "../../../wrappers/FixaButton/FixaButton";
import FixaFiltersIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/FiltersIconPath";
import { useChangedFiltersCount } from "../../utils/useChangedFiltersCount";
import { FilterRowScrollPanel } from "../FilterRowScrollPanel";
import { InputSelectPillMobile } from "./InputSelectPillMobile";
import { InputMultiSelectPillMobile } from "./InputMultiSelectPillMobile";
import { PillRadioButton } from "../FilterPill/PillRadioButton";
import { InputGroupPillMobile } from "./InputGroupPillMobile";
import { ResetFilters } from "../ResetFilters/ResetFilters";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { PillCheckbox } from "../FilterPill/PillCheckbox";

interface FilterRowMobileProps {
  filters: Filter[];
  filterValues: FilterValue[];
  onTriggerFilter: (filterId: string) => void;
  setFilterValues: (filterValues: FilterValue[]) => void;
  tracking: TrackingProps;
}

export const FilterRowMobile = ({
  filters,
  filterValues,
  onTriggerFilter,
  setFilterValues,
  tracking,
}: FilterRowMobileProps) => {
  const { t } = useTranslation();
  const changedFilterCount = useChangedFiltersCount(filters, filterValues);

  return (
    <FilterRowScrollPanel>
      {changedFilterCount > 0 && (
        <ResetFilters
          resetFilters={() => setFilterValues([])}
          tracking={tracking}
        />
      )}
      <FixaButton
        key="filter-row-mobile-button"
        type="primary"
        ssrIcon={FixaFiltersIconPath}
        text={t("filterString2", { count: changedFilterCount })}
        size="small"
        onClick={() => {
          trackerHelper.filter.trackFilterGeneralClicked(
            tracking.feature,
            tracking.mode,
            "open_modal_button"
          );
          onTriggerFilter("");
        }}
      />
      {filters
        .filter(
          ({ enabled, rowVisibility }) =>
            enabled && rowVisibility !== RowVisibility.Hidden
        )
        // Put a single grouped filter in the main filter list
        .map((filter) => {
          if (filter.inputType === InputType.Group) {
            const enabledGroupFilters = filter.filters.filter(
              ({ enabled }) => enabled
            );
            if (enabledGroupFilters.length === 1) {
              return enabledGroupFilters[0]!;
            }
          }
          return filter;
        })
        .map((filter) => {
          switch (filter.inputType) {
            case InputType.Select: {
              return (
                <InputSelectPillMobile
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  onTriggerFilter={onTriggerFilter}
                  tracking={tracking}
                />
              );
            }
            case InputType.MultiSelect: {
              return (
                <InputMultiSelectPillMobile
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  onTriggerFilter={onTriggerFilter}
                  tracking={tracking}
                />
              );
            }
            case InputType.Checkbox: {
              return (
                <PillCheckbox
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  setFilterValues={setFilterValues}
                  tracking={tracking}
                />
              );
            }
            case InputType.Group:
              return (
                <InputGroupPillMobile
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  onTriggerFilter={onTriggerFilter}
                  tracking={tracking}
                />
              );
            case InputType.RadioButton: {
              return (
                <PillRadioButton
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  setFilterValues={setFilterValues}
                  tracking={tracking}
                />
              );
            }
            default:
              return null;
          }
        })}
    </FilterRowScrollPanel>
  );
};
